import { isAxiosError } from 'axios'

import type { ServiceQueryOptionType } from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type { NationalExtWorkers, RecapExtWorkerResponse } from './types'

const RECAP_REQUEST_TIMEOUT_MS = 30_000
const RECAP_QUERY_RETRY = 3

const RECAP_EXT_WORKERS_QUERY_KEY = (key?: string | Record<string, any>) =>
  ['recap_ext_workers', key] as const

const recapGet = <T>(url: string) =>
  api.get<T>(url, { timeout: RECAP_REQUEST_TIMEOUT_MS })

const emptyRecap = (): RecapExtWorkerResponse<NationalExtWorkers> => ({
  data: { summary: [], detail: [] },
  message: 'No data found'
})

const getNationalRecapExtensionWorkers = (): ServiceQueryOptionType<
  RecapExtWorkerResponse<NationalExtWorkers>
> => ({
  retry: RECAP_QUERY_RETRY,
  queryKey: RECAP_EXT_WORKERS_QUERY_KEY({ type: 'national' }),
  queryFn: async (): Promise<RecapExtWorkerResponse<NationalExtWorkers>> => {
    try {
      const response = await recapGet<
        RecapExtWorkerResponse<NationalExtWorkers>
      >('/report/extension-personnel/recap')

      return response.data
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        return emptyRecap()
      }

      throw handleAxiosError(error)
    }
  }
})

const getProvinceRecapExtensionWorkers = (
  province_code?: string
): ServiceQueryOptionType<RecapExtWorkerResponse<NationalExtWorkers>> => ({
  retry: RECAP_QUERY_RETRY,
  queryKey: RECAP_EXT_WORKERS_QUERY_KEY({
    type: 'province',
    province_code
  }),
  queryFn: async (): Promise<RecapExtWorkerResponse<NationalExtWorkers>> => {
    try {
      const response = await recapGet<
        RecapExtWorkerResponse<NationalExtWorkers>
      >(`/report/extension-personnel/recap/province/${province_code}`)

      return response.data
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        return emptyRecap()
      }

      throw handleAxiosError(error)
    }
  }
})

const getCityRecapExtensionWorkers = ({
  province_code,
  city_code
}: {
  province_code?: string
  city_code?: string
}): ServiceQueryOptionType<RecapExtWorkerResponse<NationalExtWorkers>> => ({
  retry: RECAP_QUERY_RETRY,
  queryKey: RECAP_EXT_WORKERS_QUERY_KEY({
    type: 'city',
    province_code,
    city_code
  }),
  queryFn: async (): Promise<RecapExtWorkerResponse<NationalExtWorkers>> => {
    try {
      const response = await recapGet<
        RecapExtWorkerResponse<NationalExtWorkers>
      >(
        `/report/extension-personnel/recap/province/${province_code}/city/${city_code}`
      )

      return response.data
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        return emptyRecap()
      }

      throw handleAxiosError(error)
    }
  }
})

export const recapExtWorker = {
  queryKey: RECAP_EXT_WORKERS_QUERY_KEY,
  getNational: getNationalRecapExtensionWorkers,
  getProvince: getProvinceRecapExtensionWorkers,
  getCity: getCityRecapExtensionWorkers
}
